/**
 * Client d'API, avec file d'attente hors ligne
 *
 * L'exigence produit est claire : « consultation et cochage possibles hors
 * ligne, synchronisation ensuite ». Concrètement, deux mécanismes :
 *
 *  - **un cache de lecture** : la dernière réponse de chaque GET est conservée,
 *    et resservie quand le réseau manque. Mieux vaut un agenda de ce matin qu'un
 *    écran vide ;
 *  - **une file d'attente d'écriture** : un cochage fait dans le métro est
 *    enregistré localement et rejoué quand le réseau revient. Chaque opération
 *    porte un identifiant, et les routes concernées sont idempotentes — cocher
 *    deux fois la même échéance ne compte qu'une fois.
 *
 * Le stockage est `localStorage` plutôt qu'IndexedDB : synchrone, suffisant pour
 * un foyer, et une file d'attente qu'on peut lire à l'œil nu dans les outils de
 * développement vaut mieux qu'une base élégante et opaque.
 */

/**
 * Racine de l'API.
 *
 * Vide sur le web : l'application est servie par le même nginx que l'API, donc
 * les chemins relatifs suffisent. Renseignée pour la construction mobile
 * (`VITE_API_URL`), où la WebView Capacitor sert un bundle local et doit appeler
 * le serveur par son domaine public.
 */
export const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

/**
 * Le mobile s'authentifie par jeton porteur : depuis une origine locale, le
 * cookie `SameSite=Lax` de la session ne serait jamais envoyé. Le web garde le
 * cookie httpOnly, plus sûr, et ne stocke aucun jeton.
 */
export const MODE_JETON = BASE !== '';

const CLE_JETON = 'foyer:jeton';
const CLE_CACHE = 'foyer:cache:';
const CLE_FILE = 'foyer:file';
const CLE_SESSION = 'foyer:session';

export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    /**
     * Corps de la réponse d'erreur, quand il porte plus que le message.
     *
     * Un seul cas aujourd'hui : `/auth/google` répond 404 avec
     * `{ inconnu: true, email, prenom }` pour dire « identité valide, mais
     * aucun compte ». L'écran de connexion en a besoin pour enchaîner sur
     * « où vous mettre ? » au lieu d'afficher une erreur sèche.
     */
    readonly charge: Record<string, unknown> | null = null,
  ) {
    super(message);
  }
}

/** Levée quand l'action a été mise en file plutôt qu'envoyée. */
export class MiseEnFile extends Error {
  constructor(readonly operation: Operation) {
    super('Action enregistrée, elle partira au retour du réseau.');
  }
}

export interface Operation {
  id: string;
  methode: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  chemin: string;
  corps?: unknown;
  /** Description lisible, affichée dans le bandeau de synchronisation. */
  libelle: string;
  creeLe: string;
}

function lisJson<T>(cle: string, repli: T): T {
  try {
    const brut = localStorage.getItem(cle);
    return brut ? (JSON.parse(brut) as T) : repli;
  } catch {
    return repli;
  }
}

function ecrisJson(cle: string, valeur: unknown): void {
  try {
    localStorage.setItem(cle, JSON.stringify(valeur));
  } catch {
    // Quota dépassé : on préfère perdre le cache que planter l'application.
  }
}

// ── File d'attente ──────────────────────────────────────────────────────────

export function file(): Operation[] {
  return lisJson<Operation[]>(CLE_FILE, []);
}

function enfile(operation: Operation): void {
  ecrisJson(CLE_FILE, [...file(), operation]);
}

function defile(id: string): void {
  ecrisJson(
    CLE_FILE,
    file().filter((o) => o.id !== id),
  );
}

export function videLaFile(): void {
  localStorage.removeItem(CLE_FILE);
}

// ── Cache de lecture ────────────────────────────────────────────────────────

export function litCache<T>(chemin: string): T | null {
  return lisJson<T | null>(CLE_CACHE + chemin, null);
}

function ecrisCache(chemin: string, valeur: unknown): void {
  ecrisJson(CLE_CACHE + chemin, valeur);
}

export function videLeCache(): void {
  for (const cle of Object.keys(localStorage)) {
    if (cle.startsWith(CLE_CACHE)) localStorage.removeItem(cle);
  }
}

// ── Session locale ──────────────────────────────────────────────────────────

/** Jeton porteur, en mode mobile uniquement. */
export function jeton(): string | null {
  return MODE_JETON ? localStorage.getItem(CLE_JETON) : null;
}

export function memoriseJeton(valeur: string | null | undefined): void {
  if (!MODE_JETON) return;
  if (valeur) localStorage.setItem(CLE_JETON, valeur);
  else localStorage.removeItem(CLE_JETON);
}

/**
 * Sur le web, le jeton de session vit dans un cookie httpOnly, inaccessible
 * d'ici. On mémorise seulement *qu'une* session existe, pour éviter un
 * aller-retour inutile au démarrage.
 */
export function sessionMemorisee(): boolean {
  return localStorage.getItem(CLE_SESSION) === '1';
}

export function memoriseSession(existe: boolean): void {
  if (existe) localStorage.setItem(CLE_SESSION, '1');
  else localStorage.removeItem(CLE_SESSION);
}

// ── Requêtes ────────────────────────────────────────────────────────────────

export interface OptionsRequete {
  /** Mettre en file plutôt qu'échouer si le réseau manque. */
  differable?: boolean;
  libelle?: string;
}

async function envoie(
  methode: string,
  chemin: string,
  corps?: unknown,
): Promise<unknown> {
  const entetes: Record<string, string> = {};
  if (corps !== undefined) entetes['Content-Type'] = 'application/json';
  const porteur = jeton();
  if (porteur) entetes.Authorization = `Bearer ${porteur}`;

  const reponse = await fetch(BASE + chemin, {
    method: methode,
    headers: entetes,
    body: corps === undefined ? undefined : JSON.stringify(corps),
    // Le cookie de session voyage sur le web ; en mode jeton, il n'existe pas.
    credentials: MODE_JETON ? 'omit' : 'same-origin',
  });

  if (reponse.status === 401) {
    memoriseSession(false);
    memoriseJeton(null);
    throw new ErreurApi('Session expirée', 401);
  }

  const texte = await reponse.text();
  const charge = texte ? (JSON.parse(texte) as Record<string, unknown>) : {};
  if (!reponse.ok) {
    throw new ErreurApi((charge.erreur as string) ?? `Erreur ${reponse.status}`, reponse.status, charge);
  }
  return charge;
}

export async function lit<T>(chemin: string): Promise<T> {
  try {
    const charge = (await envoie('GET', chemin)) as T;
    ecrisCache(chemin, charge);
    return charge;
  } catch (err) {
    // Une panne réseau se rattrape sur le cache ; une erreur de l'API, non.
    if (err instanceof ErreurApi) throw err;
    const enCache = litCache<T>(chemin);
    if (enCache !== null) return enCache;
    throw err;
  }
}

export async function ecrit<T>(
  methode: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  chemin: string,
  corps?: unknown,
  options: OptionsRequete = {},
): Promise<T> {
  try {
    return (await envoie(methode, chemin, corps)) as T;
  } catch (err) {
    const reseauMuet = !(err instanceof ErreurApi);
    if (reseauMuet && options.differable) {
      const operation: Operation = {
        id: crypto.randomUUID(),
        methode,
        chemin,
        corps,
        libelle: options.libelle ?? `${methode} ${chemin}`,
        creeLe: new Date().toISOString(),
      };
      enfile(operation);
      throw new MiseEnFile(operation);
    }
    throw err;
  }
}

export interface BilanSynchronisation {
  rejouees: number;
  abandonnees: { libelle: string; motif: string }[];
}

/**
 * Rejoue la file d'attente. Une opération refusée par l'API (409 parce que
 * l'échéance a déjà été cochée sur un autre appareil, 404 parce que la tâche a
 * été supprimée) est **abandonnée** : la retenter éternellement bloquerait la
 * file. Une panne réseau, elle, laisse tout en place.
 */
export async function synchronise(): Promise<BilanSynchronisation> {
  const bilan: BilanSynchronisation = { rejouees: 0, abandonnees: [] };
  for (const operation of file()) {
    try {
      await envoie(operation.methode, operation.chemin, operation.corps);
      defile(operation.id);
      bilan.rejouees++;
    } catch (err) {
      if (err instanceof ErreurApi && err.statut >= 400 && err.statut < 500 && err.statut !== 401) {
        defile(operation.id);
        bilan.abandonnees.push({ libelle: operation.libelle, motif: err.message });
        continue;
      }
      // Réseau toujours absent ou serveur en peine : on garde la file intacte.
      break;
    }
  }
  return bilan;
}

export function oublieTout(): void {
  videLeCache();
  videLaFile();
  memoriseSession(false);
  memoriseJeton(null);
}
